/**
 * My location sharing, kept out of the way. Sharing starts by itself on a live mission (see
 * AutoShare in lib/geolocation) — nothing to tap. The screen shows at most:
 *   · a small dot on the phone map's location button (green = sharing),
 *   · a small dismissible chip only when something is wrong (location blocked, LOW ACCURACY),
 *     with "How to fix".
 * The on/off switch lives in Settings (More sheet / mission menu → ShareLocationSetting).
 */
import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import type { Id } from '@apos/shared';
import { atLeast } from '../../app/labels';
import { Button, cx, Icon, Modal, toast } from '../../components/ui';
import { autoShare, formatAccuracy, NATIVE_LOCATION_PATH, nativeMissionSync, SAFARI_LOCATION_PATH, useLocationSharing } from '../../lib/geolocation';
import { getNative } from '../../lib/native';
import { usePrefs } from '../../lib/prefs';
import { useKeepAwake } from '../../lib/wakeLock';
import { useMission } from '../../state/mission';
import { sharingView, type ShareIssue, type SharingView } from './sharingModel';
import './sharing.css';

export function isSharingHere(s: ReturnType<typeof useLocationSharing>, missionId: string | null) {
  return s.missionId === missionId && (s.status === 'active' || s.status === 'requesting');
}

/**
 * Mount once for the open mission (MissionShell): shares automatically while the mission is
 * live and the setting is on, holds the screen awake while sharing, stops when it ends.
 */
export function useAutoShareLocation() {
  const missionId = useMission((s) => s.missionId);
  const status = useMission((s) => s.bundle?.mission.status);
  const role = useMission((s) => s.bundle?.myRole);
  const enabled = usePrefs((s) => s.shareLocation);
  const target: Id | null = enabled && status === 'live' && atLeast(role, 'crew') && missionId ? missionId : null;
  const sharing = useLocationSharing();

  useEffect(() => autoShare.setTarget(target), [target]);
  useEffect(() => () => autoShare.setTarget(null), []);

  // Phones stop web GPS when the screen locks: hold the screen awake while sharing.
  useKeepAwake(target !== null && isSharingHere(sharing, target));

  // Sharing can start before the realtime socket is up, so the 'sharing_started' event (which
  // refreshes the bundle) may be missed. Keep my member row honest: the HUD uses it to measure
  // from me.
  const meSharing = useMission((s) => {
    const me = s.bundle?.me.userId;
    return s.bundle?.members.find((m) => m.userId === me)?.sharingLocation ?? false;
  });
  const active = sharing.missionId === missionId && sharing.status === 'active';

  // The iOS app (build 7+) follows this mission itself: spoken updates and location with the phone locked.
  useEffect(() => nativeMissionSync.update({ missionId: missionId ?? null, live: status === 'live', sharing: active }), [missionId, status, active]);
  useEffect(() => () => nativeMissionSync.update({ missionId: null, live: false, sharing: false }), []);
  useEffect(() => {
    if (missionId && status === 'live' && active !== meSharing) void useMission.getState().refreshBundle();
  }, [active, meSharing]); // eslint-disable-line react-hooks/exhaustive-deps

  const wasSharing = useRef(false);
  const on = isSharingHere(sharing, missionId);
  useEffect(() => {
    if (status === 'ended' && wasSharing.current) toast({ title: 'Location sharing stopped', body: 'The mission has ended.', tone: 'neutral' });
    wasSharing.current = on && status === 'live';
  }, [status, on]);
}

/** Sharing status for the open mission (dot, settings line, issue chip). */
export function useSharingView(): SharingView & { missionId: Id | null } {
  const missionId = useMission((s) => s.missionId);
  const status = useMission((s) => s.bundle?.mission.status);
  const role = useMission((s) => s.bundle?.myRole);
  const enabled = usePrefs((s) => s.shareLocation);
  const s = useLocationSharing();
  return { ...sharingView(s, { missionId, enabled, live: status === 'live', canShare: atLeast(role, 'crew') }), missionId };
}

// ───────────── help ─────────────

interface LocationUi {
  open: boolean;
  /** Chips the user closed, per mission + issue — not shown again this session. */
  dismissed: Record<string, true>;
  show(): void;
  hide(): void;
  dismiss(key: string): void;
}

const useLocationUi = create<LocationUi>((set) => ({
  open: false,
  dismissed: {},
  show: () => set({ open: true }),
  hide: () => set({ open: false }),
  dismiss: (key) => set((s) => ({ dismissed: { ...s.dismissed, [key]: true } })),
}));

export const showLocationHelp = () => useLocationUi.getState().show();

const ISSUE_TEXT: Record<ShareIssue, string> = {
  blocked: 'Location blocked',
  unsupported: 'Location unavailable',
  'low-accuracy': 'Low accuracy',
};

/** Small dismissible hint, only when something is wrong. */
export function LocationIssueChip({ className }: { className?: string }) {
  const v = useSharingView();
  const dismissed = useLocationUi((s) => s.dismissed);
  const dismiss = useLocationUi((s) => s.dismiss);
  if (!v.issue || !v.missionId) return null;
  const key = `${v.missionId}:${v.issue}`;
  if (dismissed[key]) return null;
  const tone = v.issue === 'low-accuracy' ? 'aging' : 'lost';
  return (
    <div className={cx('loc-chip', `loc-chip--${tone}`, className)} role="status" data-testid="location-issue">
      <Icon name={v.issue === 'low-accuracy' ? 'alert' : 'location-off'} size={14} />
      <span className="loc-chip__text">
        {ISSUE_TEXT[v.issue]}
        {v.issue === 'low-accuracy' && v.accuracyM ? <span className="num"> {formatAccuracy(v.accuracyM)}</span> : null}
      </span>
      <button type="button" className="loc-chip__fix" onClick={showLocationHelp} aria-label="How to fix">
        Fix
      </button>
      <button type="button" className="loc-chip__x" aria-label="Dismiss" title="Dismiss" onClick={() => dismiss(key)}>
        <Icon name="close" size={12} />
      </button>
    </div>
  );
}

/** Phone map: the location button, with a small status dot. Tap = show me (or how to fix). */
export function LocationClusterButton({ className, onLocate }: { className?: string; onLocate?(): void }) {
  const v = useSharingView();
  const label = `My location — ${v.text}`;
  return (
    <button
      type="button"
      className={cx('icon-btn icon-btn--lg icon-btn--overlay loc-btn', className)}
      aria-label={label}
      title={label}
      data-testid="location-button"
      data-sharing={v.on ? 'on' : 'off'}
      onClick={() => {
        if (v.issue && v.issue !== 'low-accuracy') showLocationHelp();
        else if (v.on) onLocate?.();
        else toast({ title: `Location sharing: ${v.text}`, body: 'Change it in More › Share my location.', tone: 'neutral' });
      }}
    >
      <Icon name={v.on ? 'location' : 'location-off'} size={22} />
      {v.on || v.issue ? <span className={cx('loc-dot', `loc-dot--${v.tone}`)} aria-hidden data-testid="location-dot" /> : null}
    </button>
  );
}

/** Desktop top bar: icon + dot while the mission is live. */
export function LocationIndicator() {
  const v = useSharingView();
  const live = useMission((s) => s.bundle?.mission.status === 'live');
  if (!live || !v.missionId) return null;
  const label = `My location — ${v.text}`;
  return (
    <button type="button" className="loc-ind" title={label} aria-label={label} data-testid="location-indicator" onClick={() => (v.issue ? showLocationHelp() : undefined)}>
      <Icon name={v.on ? 'location' : 'location-off'} size={16} />
      <span className={cx('loc-dot', 'loc-dot--inline', `loc-dot--${v.tone}`)} aria-hidden />
    </button>
  );
}

/** "How to fix" — mounted once (MissionShell). */
export function LocationHelpHost() {
  const open = useLocationUi((s) => s.open);
  const hide = useLocationUi((s) => s.hide);
  const v = useSharingView();
  const native = getNative();
  const low = v.issue === 'low-accuracy';
  return (
    <Modal open={open} onClose={hide} size="sm" eyebrow="Location" title={low ? 'Improve GPS accuracy' : 'Allow location to share it'}>
      <div className="stack-16">
        {low ? (
          <p>
            Your phone reports only a coarse position{v.accuracyM ? ` (${formatAccuracy(v.accuracyM)})` : ''}, so directions and distances from you are not reliable. Usually
            Precise Location is off, or there is no clear view of the sky.
          </p>
        ) : (
          <p>Location is blocked for Airview Studio, so the crew can't see where you are and the HUD can't measure from you.</p>
        )}
        <ul className="share-help">
          {native ? (
            <li>
              <b>iPhone:</b> {NATIVE_LOCATION_PATH}.
            </li>
          ) : (
            <>
              <li>
                <b>iPhone (Safari):</b> {SAFARI_LOCATION_PATH}. Then aA in the address bar → Website Settings → Location → Allow.
              </li>
              <li>
                <b>Android (Chrome):</b> tap the icon left of the address → Permissions → Location → Allow (and Use precise location).
              </li>
              <li>
                <b>Desktop:</b> click the icon left of the address bar → Location → Allow, then reload.
              </li>
            </>
          )}
          {low ? <li>Step outside, away from tall buildings, for a clear view of the sky.</li> : null}
        </ul>
        <p className="muted small">Your location is shared only with this mission's members, only while the mission is live, and stops when it ends. Turn it off in Settings any time.</p>
        {native ? (
          <Button
            variant="primary"
            block
            icon="settings"
            onClick={() => {
              hide();
              native.openSettings();
            }}
          >
            Open Settings
          </Button>
        ) : !low ? (
          <Button
            variant="primary"
            block
            onClick={() => {
              hide();
              autoShare.retry();
            }}
          >
            Try again
          </Button>
        ) : null}
      </div>
    </Modal>
  );
}
